import { useEffect, useState, useMemo } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Users, UserPlus, DollarSign, CalendarCheck, XCircle, UserX, Clock, Dumbbell, Sparkles, Send, AlertTriangle } from "lucide-react";
import { StatCard } from "@/components/admin/StatCard";
import { ClientDetailDialog } from "@/components/admin/ClientDetailDialog";
import { useAuth } from "@/context/AuthContext";
import { HkaBanner, FinancialReportsCard, PaymentsTable, SubscriptionsTable } from "@/components/admin/PaymentsPanel";

export function AdminDashboard() {
  const { t, tl } = useI18n();
  const [s, setS] = useState(null);
  const [bc, setBc] = useState({ title: "", message: "" });

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);

  const broadcast = async () => {
    if (!bc.title || !bc.message) return;
    try {
      const { data } = await api.post("/admin/broadcast", { ...bc, segment: "all" });
      toast.success(`${data.recipients} ✔`);
      setBc({ title: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!s) return <AdminLayout><div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div></AdminLayout>;

  return (
    <AdminLayout>
      <h1 className="mb-6 font-display text-3xl font-bold tracking-tight">{t("admin.dashboard")}</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label={t("admin.stats.activeClients")} value={s.total_clients} />
        <StatCard icon={UserPlus} label={t("admin.stats.newClients")} value={s.new_clients} />
        <StatCard icon={DollarSign} label={t("admin.stats.revenue")} value={`$${s.revenue}`} />
        <StatCard icon={CalendarCheck} label={t("admin.stats.reservations")} value={s.reservations} />
        <StatCard icon={XCircle} label={t("admin.stats.cancellations")} value={s.cancellations} />
        <StatCard icon={UserX} label={t("admin.stats.noShows")} value={s.no_shows} />
        <StatCard icon={Clock} label={t("admin.stats.pendingPayments")} value={s.pending_payments} />
        <StatCard icon={Dumbbell} label={t("admin.stats.fitnessRev")} value={`$${s.fitness_revenue}`} />
        <StatCard icon={Sparkles} label={t("admin.stats.pilatesRev")} value={`$${s.pilates_revenue}`} />
      </div>

      {s.popular_classes?.length > 0 && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">{t("admin.stats.popular")}</h2>
          <div className="space-y-2">
            {s.popular_classes.map((c, i) => (
              <div key={`${tl(c.name)}-${i}`} className="flex items-center justify-between text-sm">
                <span>{tl(c.name)}</span>
                <span className="font-semibold">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 max-w-lg rounded-2xl border border-border bg-card p-6">
        <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold"><Send className="h-5 w-5" />{t("admin.broadcast")}</h2>
        <div className="space-y-3">
          <Input data-testid="broadcast-title" placeholder="Título" value={bc.title} onChange={(e) => setBc({ ...bc, title: e.target.value })} className="h-11" />
          <Textarea data-testid="broadcast-message" placeholder="Mensaje" value={bc.message} onChange={(e) => setBc({ ...bc, message: e.target.value })} />
          <Button data-testid="broadcast-send" onClick={broadcast} className="rounded-full">{t("admin.broadcast")}</Button>
        </div>
      </div>
    </AdminLayout>
  );
}

export function AdminClients() {
  const { t, tl } = useI18n();
  const { user: me } = useAuth();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [goalW, setGoalW] = useState("");
  const [goalS, setGoalS] = useState("");
  const [sortBy, setSortBy] = useState("name");        // name | pack | activity
  const [filterActivity, setFilterActivity] = useState("all");
  const [filterPack, setFilterPack] = useState("all");
  const [anomalies, setAnomalies] = useState([]);
  const [mismatches, setMismatches] = useState([]);
  const loadMismatches = () => api.get("/admin/anomalies/credit-mismatch").then(({ data }) => setMismatches(data.items || [])).catch(() => {});
  const fixMismatch = async (m) => {
    try {
      const { data } = await api.post(`/admin/reservations/${m.reservation_id}/reassign-credit`);
      toast.success(tl({ es: `Corregido: crédito ${data.required_type} descontado`, en: `Fixed: ${data.required_type} credit consumed`, fr: `Corrigé : crédit ${data.required_type} décompté` }));
      loadMismatches(); api.get("/admin/clients").then(({ data }) => setClients(data));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.code === "no_valid_credit" ? tl({ es: `El cliente no tiene ningún forfait ${d.required_type} activo. Asígnale uno y vuelve a intentar.`, en: `Client has no active ${d.required_type} pack. Grant one and retry.`, fr: `Le client n'a aucun forfait ${d.required_type} actif. Attribuez-en un puis réessayez.` }) : formatApiError(d), { duration: 8000 });
    }
  };
  const loadAnomalies = () => api.get("/admin/anomalies/double-credits").then(({ data }) => setAnomalies(data.items || [])).catch(() => {});

  const ACTIVITY_LABEL = {
    fitness: "Fitness", calisthenics: "Calisthenics", youth: "Teens & Kids",
    private: tl({ es: "Fitness privado", en: "Private fitness", fr: "Fitness privé" }),
    pilates_group: tl({ es: "Pilates grupal", en: "Group Pilates", fr: "Pilates collectif" }),
    pilates_private: tl({ es: "Pilates privado", en: "Private Pilates", fr: "Pilates privé" }),
  };
  const actLabel = (a) => (a ? (ACTIVITY_LABEL[a] || a) : "—");

  const activityOptions = useMemo(
    () => Array.from(new Set(clients.map((c) => c.activity).filter(Boolean))).sort(),
    [clients]);
  const packOptions = useMemo(
    () => Array.from(new Set(clients.map((c) => c.active_pack).filter(Boolean))).sort(),
    [clients]);

  const displayedClients = useMemo(() => {
    let list = clients.filter((c) =>
      (filterActivity === "all" || c.activity === filterActivity) &&
      (filterPack === "all" || c.active_pack === filterPack));
    const byName = (a, b) => (a.name || "").trim().toLowerCase().localeCompare((b.name || "").trim().toLowerCase());
    list = [...list].sort((a, b) => {
      if (sortBy === "pack") return (a.active_pack || "~").localeCompare(b.active_pack || "~") || byName(a, b);
      if (sortBy === "activity") return actLabel(a.activity).localeCompare(actLabel(b.activity)) || byName(a, b);
      return byName(a, b);
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, sortBy, filterActivity, filterPack]);

  useEffect(() => { if (detail?.user) { setGoalW(detail.user.weight_goal ?? ""); setGoalS(detail.user.steps_goal ?? ""); } }, [detail?.user]);
  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data));
    api.get("/products?active_only=false").then(({ data }) => setProducts(data));
    loadAnomalies(); loadMismatches();
  }, []);

  const grantPack = async (product_id, method) => {
    const body = { product_id, method };
    if (method === "comp") body.amount = 0;
    try {
      if (method === "saved_card") {
        if (!window.confirm(tl({ es: "¿Cobrar ahora la tarjeta guardada del cliente por este forfait?", en: "Charge the client's saved card for this pack now?", fr: "Débiter maintenant la carte enregistrée du client pour ce forfait ?" }))) return;
        const { data } = await api.post(`/admin/clients/${detail.user.user_id}/charge-card`, { product_id });
        if (!data.ok) { toast.error(`${tl({ es: "Cobro rechazado", en: "Charge declined", fr: "Paiement refusé" })}: ${data.reason}`, { duration: 9000 }); return; }
        toast.success(tl({ es: `Tarjeta cobrada ($${data.amount}) y forfait añadido`, en: `Card charged ($${data.amount}) and pack added`, fr: `Carte débitée (${data.amount} $) et forfait ajouté` }));
      } else {
        await api.post(`/admin/clients/${detail.user.user_id}/grant-pack`, body);
        toast.success(tl({ es: "Forfait añadido", en: "Pack added", fr: "Forfait ajouté" }));
      }
      const { data } = await api.get(`/admin/clients/${detail.user.user_id}`);
      setDetail({ ...data, name: detail.name });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const adjustCredit = async (credit_id, delta) => {
    try {
      await api.post(`/admin/credits/${credit_id}/adjust`, { delta });
      const { data } = await api.get(`/admin/clients/${detail.user.user_id}`);
      setDetail({ ...data, name: detail.name });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancelSub = async (sub) => {
    if (!window.confirm(tl({
      es: "¿Cancelar la renovación de este abono? No se volverá a cobrar. Los créditos restantes siguen válidos hasta su vencimiento.",
      en: "Cancel this subscription's renewal? It won't be charged again. Remaining credits stay valid until expiry.",
      fr: "Annuler le renouvellement de cet abonnement ? Il ne sera plus débité. Les crédits restants restent valides jusqu'à expiration.",
    }))) return;
    try {
      await api.post(`/admin/subscriptions/${sub.subscription_id}/cancel`);
      toast.success(tl({ es: "Renovación cancelada", en: "Renewal cancelled", fr: "Renouvellement annulé" }));
      const { data } = await api.get(`/admin/clients/${detail.user.user_id}`);
      setDetail({ ...data, name: detail.name });
      api.get("/admin/clients").then(({ data: cs }) => setClients(cs));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setRole = async (role, universe) => {
    try {
      await api.post(`/admin/clients/${detail.user.user_id}/set-role`, { role, universe });
      toast.success(role === "coach"
        ? tl({ es: "Ahora es coach", en: "Now a coach", fr: "Maintenant coach" })
        : tl({ es: "Vuelto a cliente", en: "Back to client", fr: "Repassé en client" }));
      const { data } = await api.get(`/admin/clients/${detail.user.user_id}`);
      setDetail({ ...data, name: detail.name });
      api.get("/admin/clients").then(({ data }) => setClients(data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setDiscount = async (percent) => {
    try {
      await api.post(`/admin/clients/${detail.user.user_id}/discount`, { percent });
      toast.success(tl({ es: "Descuento guardado", en: "Discount saved", fr: "Remise enregistrée" }));
      const { data } = await api.get(`/admin/clients/${detail.user.user_id}`);
      setDetail({ ...data, name: detail.name });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveGoals = async () => {
    const body = {};
    if (goalW !== "") body.weight_goal = parseFloat(goalW);
    if (goalS !== "") body.steps_goal = parseInt(goalS, 10);
    if (!Object.keys(body).length) return;
    try {
      await api.post(`/admin/clients/${detail.user.user_id}/goals`, body);
      toast.success(tl({ es: "Objetivos guardados", en: "Goals saved", fr: "Objectifs enregistrés" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleOnline = async (c, e) => {
    e.stopPropagation();
    try {
      await api.post(`/admin/clients/${c.user_id}/online`, { is_online: !c.is_online });
      setClients((prev) => prev.map((x) => (x.user_id === c.user_id ? { ...x, is_online: !c.is_online } : x)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const validateHealth = async (u) => {
    try {
      await api.post(`/admin/clients/${u.user_id}/validate-health`);
      toast.success(tl({ es: "Cuestionario validado", en: "Questionnaire validated", fr: "Questionnaire validé" }));
      const { data } = await api.get(`/admin/clients/${u.user_id}`);
      setDetail({ ...data, name: detail.name });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (c) => {
    setDetail({ loading: true, name: c.name });
    try { const { data } = await api.get(`/admin/clients/${c.user_id}`); setDetail({ ...data, name: c.name }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDetail(null); }
  };

  const deleteClient = async (u) => {
    if (!window.confirm(tl({
      es: `¿Eliminar DEFINITIVAMENTE a ${u.name}? Esta acción no se puede deshacer. El historial de pagos se conserva.`,
      en: `PERMANENTLY delete ${u.name}? This cannot be undone. Payment history is kept.`,
      fr: `Supprimer DÉFINITIVEMENT ${u.name} ? Action irréversible. L'historique des paiements est conservé.`,
    }))) return;
    try {
      await api.delete(`/admin/clients/${u.user_id}`);
      toast.success(tl({ es: "Cliente eliminado", en: "Client deleted", fr: "Client supprimé" }));
      setDetail(null);
      api.get("/admin/clients").then(({ data }) => setClients(data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePack = async (c) => {
    const left = c.credits_remaining ?? 0;
    if (!window.confirm(tl({
      es: `¿Eliminar este forfait? Se perderán ${left} sesión(es) restante(s). Acción irreversible.`,
      en: `Delete this pack? ${left} remaining session(s) will be lost. This cannot be undone.`,
      fr: `Supprimer ce forfait ? ${left} séance(s) restante(s) seront perdues. Action irréversible.`,
    }))) return;
    try {
      await api.delete(`/admin/credits/${c.credit_id}`);
      toast.success(tl({ es: "Forfait eliminado", en: "Pack deleted", fr: "Forfait supprimé" }));
      if (detail?.user) {
        const { data } = await api.get(`/admin/clients/${detail.user.user_id}`);
        setDetail({ ...data, name: detail.name });
      }
      loadAnomalies();
      api.get("/admin/clients").then(({ data }) => setClients(data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <h1 className="mb-6 font-display text-3xl font-bold tracking-tight">{t("admin.clients")}</h1>

      {me?.role === "admin" && mismatches.length > 0 && (
        <div data-testid="credit-mismatch-alert" className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            {tl({ es: "Reservas con forfait incorrecto", en: "Bookings on the wrong pack", fr: "Réservations sur le mauvais forfait" })} ({mismatches.length})
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            {tl({ es: "Clases reservadas con un forfait que no corresponde a la clase (ej. clase Baby 3-5 con pack adulto) o sin descontar crédito. « Corregir » devuelve el crédito usado y descuenta el forfait correcto.",
                  en: "Classes booked with a pack that doesn't match the class (e.g. Baby 3-5 class on an adult pack) or without consuming a credit. “Fix” refunds the wrong credit and consumes the right pack.",
                  fr: "Cours réservés avec un forfait qui ne correspond pas (ex. cours Baby 3-5 sur un pack adulte) ou sans décompte. « Corriger » rembourse le crédit utilisé et décompte le bon forfait." })}
          </p>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {mismatches.map((m) => (
              <div key={m.reservation_id} data-testid={`mismatch-${m.reservation_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-background px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{m.user_name}</span>
                  <span className="text-muted-foreground"> · {m.class_name} · {m.start ? new Date(m.start).toLocaleDateString(undefined, { timeZone: "America/Panama" }) : ""} · {tl({ es: "requiere", en: "needs", fr: "requiert" })} <b>{m.required_type}</b> · {m.reason === "no_credit" ? tl({ es: "sin crédito descontado", en: "no credit consumed", fr: "aucun crédit décompté" }) : `${tl({ es: "usó", en: "used", fr: "a utilisé" })} ${m.used_type}${m.used_product ? ` (${m.used_product})` : ""}`}</span>
                </span>
                <Button data-testid={`mismatch-fix-${m.reservation_id}`} size="sm" variant="outline" onClick={() => fixMismatch(m)} className="h-8 rounded-full">
                  {tl({ es: "Corregir", en: "Fix", fr: "Corriger" })}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {me?.role === "admin" && anomalies.length > 0 && (
        <div data-testid="double-credit-alert" className="mb-6 rounded-2xl border border-destructive/40 bg-destructive/5 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-destructive">
            <AlertTriangle className="h-4 w-4" />
            {tl({ es: "Créditos duplicados detectados", en: "Duplicate credits detected", fr: "Crédits en double détectés" })} ({anomalies.length})
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            {tl({ es: "Estos forfaits parecen creados por duplicado en un mismo pago. Revisa y elimina el sobrante.",
                  en: "These packs look like duplicates from a single payment. Review and remove the extra one.",
                  fr: "Ces forfaits semblent créés en double sur un même paiement. Vérifiez et supprimez celui en trop." })}
          </p>
          <div className="space-y-1.5">
            {anomalies.map((a) => (
              <div key={a.credit_id} data-testid={`anomaly-${a.credit_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-background px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{a.user_name}</span>
                  <span className="text-muted-foreground"> · {a.credit_type} · {a.credits_remaining}/{a.credits_total}{a.purchased_at ? ` · ${new Date(a.purchased_at).toLocaleDateString(undefined, { timeZone: "America/Panama" })}` : ""}</span>
                </span>
                <Button data-testid={`anomaly-delete-${a.credit_id}`} size="sm" variant="outline"
                  onClick={() => deletePack(a)}
                  className="h-8 rounded-full border-destructive text-destructive hover:bg-destructive/10">
                  {tl({ es: "Eliminar sobrante", en: "Remove extra", fr: "Supprimer le doublon" })}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{tl({ es: "Ordenar por", en: "Sort by", fr: "Trier par" })}</span>
          <select data-testid="clients-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm">
            <option value="name">{tl({ es: "Alfabético", en: "Alphabetical", fr: "Alphabétique" })}</option>
            <option value="pack">{tl({ es: "Forfait usado", en: "Pack used", fr: "Forfait utilisé" })}</option>
            <option value="activity">{tl({ es: "Actividad", en: "Activity", fr: "Activité" })}</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{tl({ es: "Actividad", en: "Activity", fr: "Activité" })}</span>
          <select data-testid="clients-filter-activity" value={filterActivity} onChange={(e) => setFilterActivity(e.target.value)}
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm">
            <option value="all">{tl({ es: "Todas", en: "All", fr: "Toutes" })}</option>
            {activityOptions.map((a) => <option key={a} value={a}>{actLabel(a)}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{tl({ es: "Forfait", en: "Pack", fr: "Forfait" })}</span>
          <select data-testid="clients-filter-pack" value={filterPack} onChange={(e) => setFilterPack(e.target.value)}
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm">
            <option value="all">{tl({ es: "Todos", en: "All", fr: "Tous" })}</option>
            {packOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <span className="text-xs text-muted-foreground">{displayedClients.length} {tl({ es: "clientes", en: "clients", fr: "clients" })}</span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/40 text-left"><tr>
            <th className="p-3">Nombre</th><th className="p-3">Email</th>
            <th className="p-3">{tl({ es: "Forfait", en: "Pack", fr: "Forfait" })}</th>
            <th className="p-3">{tl({ es: "Clases", en: "Classes", fr: "Classes" })}</th>
            <th className="p-3">{tl({ es: "Actividad", en: "Activity", fr: "Activité" })}</th>
            <th className="p-3">Rol</th><th className="p-3">Salud</th>
            <th className="p-3">{tl({ es: "En línea", en: "Online", fr: "En ligne" })}</th>
          </tr></thead>
          <tbody>
            {displayedClients.map((c) => (
              <tr key={c.user_id} data-testid={`client-row-${c.user_id}`} onClick={() => openDetail(c)} className="cursor-pointer border-b border-border/60 hover:bg-accent/40">
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-muted-foreground">{c.email}</td>
                <td className="p-3" data-testid={`client-pack-${c.user_id}`}>{c.active_pack || "—"}</td>
                <td className="p-3 font-medium" data-testid={`client-classes-${c.user_id}`}>{c.has_pack ? `${c.pack_remaining}/${c.pack_total}` : "—"}</td>
                <td className="p-3" data-testid={`client-activity-${c.user_id}`}>{actLabel(c.activity)}</td>
                <td className="p-3"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{c.role}</span></td>
                <td className="p-3"><span className={`text-xs ${c.health_completed ? "text-primary" : "text-destructive"}`}>{c.health_flag}</span></td>
                <td className="p-3">
                  <button data-testid={`toggle-online-${c.user_id}`} onClick={(e) => toggleOnline(c, e)}
                    className={`relative h-6 w-11 rounded-full transition-colors ${c.is_online ? "bg-primary" : "bg-secondary"}`} aria-label="toggle online">
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${c.is_online ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ClientDetailDialog detail={detail} onClose={() => setDetail(null)}
        goalW={goalW} setGoalW={setGoalW} goalS={goalS} setGoalS={setGoalS} saveGoals={saveGoals}
        products={products} onGrant={grantPack} onAdjust={adjustCredit} onSetRole={setRole}
        onSetDiscount={me?.role === "admin" ? setDiscount : undefined}
        onCancelSub={me?.role === "admin" ? cancelSub : undefined}
        onDelete={me?.role === "admin" ? deleteClient : undefined}
        onDeletePack={me?.role === "admin" ? deletePack : undefined}
        onValidateHealth={validateHealth} />
    </AdminLayout>
  );
}

export function AdminPayments() {
  const { t, tl } = useI18n();
  const [payments, setPayments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [hka, setHka] = useState(null);
  const [rFrom, setRFrom] = useState("");
  const [rTo, setRTo] = useState("");

  const reportUrl = (fmt) => {
    const qs = new URLSearchParams();
    if (rFrom) qs.set("from", rFrom);
    if (rTo) qs.set("to", rTo);
    const q = qs.toString();
    return `${API}/admin/reports/${fmt}${q ? `?${q}` : ""}`;
  };
  const load = () => {
    api.get("/admin/payments").then(({ data }) => setPayments(data));
    api.get("/admin/subscriptions").then(({ data }) => setSubs(data));
  };
  useEffect(() => { load(); api.get("/admin/hka/status").then(({ data }) => setHka(data)).catch(() => {}); }, []);

  const emitInvoice = async (id) => {
    try {
      const { data } = await api.post(`/admin/payments/${id}/invoice`);
      if (data.status === "authorized") toast.success(`Factura ${data.doc_number} · CUFE ${(data.cufe || "").slice(0, 12)}…`);
      else toast.error(`HKA: ${data.message || "error"}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "hka_not_configured" ? tl({ es: "Configura las credenciales HKA primero.", en: "Configure HKA credentials first.", fr: "Configurez d'abord les identifiants HKA." }) : formatApiError(d));
    }
  };
  const sendInvoice = async (id) => {
    try {
      const { data } = await api.post(`/admin/payments/${id}/invoice/send`);
      toast.success(tl({ es: `Factura enviada a ${data.to}`, en: `Invoice sent to ${data.to}`, fr: `Facture envoyée à ${data.to}` }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const confirm = async (id) => { try { await api.post(`/admin/payments/${id}/confirm`); toast.success(t("common.success")); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const refund = async (id) => {
    if (!window.confirm(tl({
      es: "¿Reembolsar este pago? Si fue pagado con tarjeta (Paguelo Fácil), se intentará devolver el dinero automáticamente.",
      en: "Refund this payment? If paid by card (Paguelo Fácil), the money will be returned automatically.",
      fr: "Rembourser ce paiement ? S'il a été payé par carte (Paguelo Fácil), l'argent sera renvoyé automatiquement.",
    }))) return;
    try {
      const { data } = await api.post(`/admin/payments/${id}/refund`);
      toast.success(data.gateway_refund
        ? tl({ es: "Reembolso enviado a Paguelo Fácil.", en: "Refund sent to Paguelo Fácil.", fr: "Remboursement envoyé à Paguelo Fácil." })
        : t("common.success"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { duration: 9000 });
    }
  };
  const subAction = async (id, action) => {
    if (action === "charge-now" && !window.confirm(tl({ es: "¿Cobrar ahora la tarjeta del cliente?", en: "Charge the client's card now?", fr: "Prélever la carte du client maintenant ?" }))) return;
    if (action === "send-payment-link" && !window.confirm(tl({ es: "¿Enviar al cliente un enlace de pago por email? Al pagar, sus créditos se activan y su nueva tarjeta queda registrada.", en: "Email the client a payment link? Paying activates credits and saves the new card.", fr: "Envoyer un lien de paiement par e-mail ? Le paiement active les crédits et enregistre la nouvelle carte." }))) return;
    try {
      const { data } = await api.post(`/admin/subscriptions/${id}/${action}`);
      if (action === "charge-now" && !data.ok) toast.error(`${tl({ es: "Cobro rechazado", en: "Charge declined", fr: "Prélèvement refusé" })}: ${data.reason || "-"}`, { duration: 9000 });
      else if (action === "send-payment-link") toast.success(tl({ es: `Enlace enviado a ${data.to}`, en: `Link sent to ${data.to}`, fr: `Lien envoyé à ${data.to}` }));
      else toast.success(t("common.success"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runRenewals = async () => { try { const { data } = await api.post("/admin/subscriptions/run-renewals"); toast.success(`${data.invoices_created} facturas creadas`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const runAlerts = async () => { try { const { data } = await api.post("/admin/run-alerts"); toast.success(`Alertas: ${data.expiring} exp · ${data.low} créditos · ${data.inactive} inactivos`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("admin.payments")}</h1>
        <a href={`${API}/admin/payments/export`} data-testid="export-csv-btn" className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent">{t("admin.export")}</a>
      </div>

      <HkaBanner hka={hka} />
      <FinancialReportsCard rFrom={rFrom} setRFrom={setRFrom} rTo={rTo} setRTo={setRTo} reportUrl={reportUrl} />
      <PaymentsTable payments={payments} onConfirm={confirm} onRefund={refund} onEmit={emitInvoice} onSendInvoice={sendInvoice} />

      <div className="mb-4 mt-10 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold tracking-tight">{tl({ es: "Suscripciones", en: "Subscriptions", fr: "Abonnements" })}</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="run-alerts-btn" onClick={runAlerts} className="rounded-full">
            {tl({ es: "Enviar alertas", en: "Send alerts", fr: "Envoyer alertes" })}
          </Button>
          <Button size="sm" variant="outline" data-testid="run-renewals-btn" onClick={runRenewals} className="rounded-full">
            {tl({ es: "Generar renovaciones", en: "Run renewals", fr: "Générer renouvellements" })}
          </Button>
        </div>
      </div>
      <SubscriptionsTable subs={subs} onAction={subAction} />
    </AdminLayout>
  );
}
